import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ImageIcon, RefreshCw, XCircle } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import RequireAdmin from "@/components/admin/RequireAdmin";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useAdminI18n } from "@/lib/adminI18n";

function AdminSocialPostsPageInner() {
  const { apiFetch } = useAdminAuth();
  const { t } = useAdminI18n();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [mode, setMode] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const [loadError, setLoadError] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), 25000) : null;
    try {
      const res = await apiFetch("/admin/social/queue?status=pending&limit=40", ctrl ? { signal: ctrl.signal } : {});
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || t("social.loadFailed", "Could not load posts"));
      setItems(Array.isArray(data.items) ? data.items : []);
      setCounts(data.counts || {});
      setMode(data.mode || "");
    } catch (err) {
      const aborted = err?.name === "AbortError";
      const message = aborted
        ? t("social.loadTimeout", "The queue took too long. Tap Refresh.")
        : (err?.message || t("social.loadFailed", "Could not load posts"));
      setLoadError(message);
      toast.error(message);
    } finally {
      if (timer) clearTimeout(timer);
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (item, decision) => {
    const pid = item.product_id;
    setBusyId(pid);
    try {
      const res = await apiFetch(`/admin/social/queue/${encodeURIComponent(pid)}/${decision}`, {
        method: "POST",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || t("social.decisionFailed", "Could not update post"));
      setItems((rows) => rows.filter((row) => row.product_id !== pid));
      setCounts((prev) => ({
        ...prev,
        pending: Math.max(0, Number(prev.pending || 0) - 1),
        posted: Number(prev.posted || 0) + (decision === "approve" ? 1 : 0),
        skipped: Number(prev.skipped || 0) + (decision === "reject" ? 1 : 0),
      }));
      toast.success(
        decision === "approve"
          ? t("social.approved", "Posted to Shop For Less Instagram and Facebook")
          : t("social.rejected", "Skipped. This product will not be posted."),
      );
    } catch (err) {
      toast.error(err?.message || t("social.decisionFailed", "Could not update post"));
    } finally {
      setBusyId("");
    }
  };

  return (
    <AdminShell
      title={t("social.title", "Shop For Less posts")}
      description={t(
        "social.description",
        "Preview in-stock products, then approve to post on Instagram and Facebook or reject to skip. Nothing is posted until you approve.",
      )}
      actions={(
        <button type="button" onClick={load} className="admin-shell-action">
          <RefreshCw className="h-4 w-4" />
          <span>{t("dashboard.refresh", "Refresh")}</span>
        </button>
      )}
    >
      <div className="mb-5 flex flex-wrap gap-3 text-sm text-white/70">
        <span>Waiting: {counts.pending ?? "—"}</span>
        <span>Posted: {counts.posted ?? "—"}</span>
        <span>Skipped: {counts.skipped ?? "—"}</span>
        {mode ? <span>Mode: {mode}</span> : null}
      </div>
      {loading ? (
        <p className="text-white/60">{t("social.loading", "Loading queue…")}</p>
      ) : loadError ? (
        <p className="text-red-300" data-testid="social-load-error">{loadError}</p>
      ) : items.length === 0 ? (
        <p className="text-white/60">{t("social.empty", "No products waiting for review.")}</p>
      ) : (
        <div className="grid gap-5">
          {items.map((item) => {
            const images = item.image_urls || [];
            const busy = busyId === item.product_id;
            return (
              <article
                key={item.product_id}
                className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.045]"
                data-testid={`social-card-${item.product_id}`}
              >
                <div className="grid gap-0 lg:grid-cols-[220px_1fr]">
                  <div className="flex gap-2 overflow-x-auto bg-black/30 p-3">
                    {images.length ? images.slice(0, 6).map((src) => (
                      <img
                        key={src}
                        src={src}
                        alt=""
                        className="h-40 w-32 shrink-0 rounded-xl object-cover"
                      />
                    )) : (
                      <div className="grid h-40 w-full place-items-center text-white/30">
                        <ImageIcon className="h-8 w-8" />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <p className="text-xs uppercase tracking-[0.2em] text-white/40">#{item.product_id}</p>
                    <h2 className="mt-1 text-lg font-semibold text-white">{item.name || "Untitled"}</h2>
                    {item.price != null ? <p className="mt-1 text-[#F58D4A]">${item.price}</p> : null}
                    <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-6 text-white/75">{item.caption}</pre>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => decide(item, "approve")}
                        className="inline-flex items-center gap-2 rounded-xl bg-[#F37021] px-4 py-2 text-sm font-semibold text-black disabled:opacity-50"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        {busy ? t("social.posting", "Posting…") : t("social.approve", "Approve & post")}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => decide(item, "reject")}
                        className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                      >
                        <XCircle className="h-4 w-4" />
                        {t("social.reject", "Reject")}
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
}

export default function AdminSocialPostsPage() {
  return (
    <RequireAdmin permission="products.manage">
      <AdminSocialPostsPageInner />
    </RequireAdmin>
  );
}
